import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { contextSourceService, ContextSourcePayload, ContextSourceResponse } from "../../services/agent/ContextSourceService";

export function useContextSource(agentId: string | undefined) {
  return useQuery<ContextSourceResponse | null>({
    queryKey: ["agent-context-source", agentId],
    queryFn: () => {
      if (!agentId) return Promise.resolve(null);
      return contextSourceService.getForAgent(agentId);
    },
    enabled: !!agentId,
  });
}

export function useContextSources() {
  return useQuery<ContextSourceResponse[]>({
    queryKey: ["context-sources"],
    queryFn: () => contextSourceService.listUserSources(),
    staleTime: 0,
  });
}

export function useCreateContextSource() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: ContextSourcePayload) => contextSourceService.create(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["context-sources"] });
    },
    onError: (error: any) => {
      const message = error?.response?.data?.error?.message || error?.message || "Failed to create integration";
      toast.error(message);
    },
  });
}

export function useAttachContextSource() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ agentId, sourceId }: { agentId: string; sourceId: number }) =>
      contextSourceService.attachToAgent(agentId, sourceId),
    onSuccess: (_, { agentId }) => {
      queryClient.invalidateQueries({ queryKey: ["agent-context-source", agentId] });
    },
  });
}

export function useUpsertContextSource() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ agentId, sourceId, payload }: { agentId: string; sourceId: number; payload: ContextSourcePayload }) =>
      contextSourceService.upsert(agentId, sourceId, payload),
    onSuccess: (_, { agentId }) => {
      queryClient.invalidateQueries({ queryKey: ["agent-context-source", agentId] });
    },
    onError: (error: any) => {
      const message = error?.response?.data?.error?.message || error?.message || "Failed to update integration";
      toast.error(message);
    },
  });
}

export function useDeleteContextSource() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (sourceId: string) => contextSourceService.removeSource(sourceId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["context-sources"] });
    },
  });
}

export function useDetachContextSource() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ agentId, sourceId }: { agentId: string; sourceId?: number }) =>
      contextSourceService.remove(agentId, sourceId),
    onSuccess: (_, { agentId }) => {
      queryClient.invalidateQueries({ queryKey: ["agent-context-source", agentId] });
      queryClient.invalidateQueries({ queryKey: ["context-sources"] });
    },
  });
}

export function useRefreshContextSourceCapabilities() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (agentId: string) => contextSourceService.refreshCapabilities(agentId),
    onSuccess: (_, agentId) => {
      queryClient.invalidateQueries({ queryKey: ["agent-context-source", agentId] });
    },
    onError: (error: any) => {
      const message = error?.response?.data?.error?.message || error?.message || "Failed to refresh capabilities";
      toast.error(message);
    },
  });
}

export function useToggleContextSourceActive() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ sourceId, isActive }: { sourceId: number; isActive: boolean }) =>
      contextSourceService.toggleActive(sourceId, isActive),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["context-sources"] });
    },
  });
}
